import { useEffect, useState } from 'react'

/**
 * Subscribes to a CSS media query. Safe to call where `window` is unavailable (returns false).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.matchMedia(query).matches
    } catch {
      return false
    }
  })

  useEffect(() => {
    let mql: MediaQueryList
    try {
      mql = window.matchMedia(query)
    } catch {
      return
    }
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches)
    setMatches(mql.matches)
    mql.addEventListener?.('change', onChange)
    return () => mql.removeEventListener?.('change', onChange)
  }, [query])

  return matches
}

/**
 * True when the primary input can hover (mouse or trackpad). On touch screens a tap
 * triggers hover states, so hover-only motion must be disabled there to avoid jumps.
 */
export function useHoverCapable(): boolean {
  return useMediaQuery('(hover: hover) and (pointer: fine)')
}

/**
 * True on phone-sized viewports, where reveals use shorter travel and durations.
 */
export function useCompactViewport(): boolean {
  return useMediaQuery('(max-width: 767px)')
}
